import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import useAuth from "../hooks/useAuth";
import { hasActiveSubscription } from "../utils/subscriptionUtils";

const CartContext = createContext({});

const CART_STORAGE_KEY = "shotkut_cart";

export const CartProvider = ({ children }) => {
  const { auth } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [lastRemovedOwned, setLastRemovedOwned] = useState([]);
  const lastRemovedOwnedRef = useRef([]);

  // Load cart from localStorage on mount (migrate legacy items with resolution)
  useEffect(() => {
    try {
      const stored = localStorage.getItem(CART_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          const seen = new Map();
          const normalized = [];
          for (const row of parsed) {
            const vid = row.videoID || row.video?._id;
            if (!vid) continue;
            const key = String(vid);
            if (seen.has(key)) continue;
            seen.set(key, true);
            normalized.push({
              videoID: vid,
              video: row.video,
              addedAt: row.addedAt,
            });
          }
          setCartItems(normalized);
        }
      }
    } catch (e) {
      console.error("Error loading cart from localStorage:", e);
    }
  }, []);

  // Persist cart to localStorage on every change
  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
    } catch (e) {
      console.error("Error saving cart to localStorage:", e);
    }
  }, [cartItems]);

  // Fetch the authoritative cart from server. Server will auto-remove any
  // already-owned videos from the cart and report them via `removedOwned`.
  // Returns { success, removedOwned } so the caller can show a toast.
  const revalidateCart = useCallback(async () => {
    if (!auth?.RawToken) {
      return { success: true, removedOwned: [] };
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/cart`,
        {
          headers: {
            Authorization: `Bearer ${auth.RawToken}`,
          },
        }
      );

      if (response.data.success && Array.isArray(response.data.cart)) {
        const serverCart = response.data.cart.map((item) => ({
          videoID: item.videoID,
          video: item.video,
          basePrice: item.basePrice,
          addedAt: item.addedAt,
        }));
        setCartItems(serverCart);

        const removedOwned = Array.isArray(response.data.removedOwned)
          ? response.data.removedOwned
          : [];
        if (removedOwned.length > 0) {
          lastRemovedOwnedRef.current = removedOwned;
          setLastRemovedOwned(removedOwned);
        }
        return { success: true, removedOwned };
      }
      return { success: false, removedOwned: [] };
    } catch (error) {
      console.error("Error revalidating cart:", error);
      return { success: false, removedOwned: [] };
    }
  }, [auth?.RawToken]);

  // Consume + clear the latest "auto-removed because already owned" list.
  // Lets pages display a toast once and avoid showing it twice.
  const consumeRemovedOwned = useCallback(() => {
    const removed = lastRemovedOwnedRef.current;
    lastRemovedOwnedRef.current = [];
    setLastRemovedOwned([]);
    return removed;
  }, []);

  // Clear cart when user has an active subscription (one-time flow disabled)
  useEffect(() => {
    if (!hasActiveSubscription(auth?.subscription)) return;

    setCartItems([]);
    try {
      localStorage.removeItem(CART_STORAGE_KEY);
    } catch (e) {
      console.error("Error clearing cart localStorage:", e);
    }

    if (auth?.RawToken) {
      axios
        .post(
          `${process.env.REACT_APP_BACKEND_URL}/cart/clear`,
          {},
          { headers: { Authorization: `Bearer ${auth.RawToken}` } },
        )
        .catch((error) => console.error("Error clearing server cart:", error));
    }
  }, [auth?.subscription, auth?.RawToken]);

  // Sync cart to server when user logs in
  useEffect(() => {
    const syncCartOnLogin = async () => {
      if (!auth?.RawToken) return;
      if (hasActiveSubscription(auth?.subscription)) return;

      try {
        const localCart = JSON.parse(localStorage.getItem(CART_STORAGE_KEY) || "[]");

        if (localCart.length > 0) {
          const syncItems = localCart.map((item) => ({
            videoID: item.videoID || item.video?._id,
          }));

          await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/cart/sync`,
            { items: syncItems },
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${auth.RawToken}`,
              },
            }
          );
        }

        await revalidateCart();
      } catch (error) {
        console.error("Error syncing cart:", error);
      }
    };

    syncCartOnLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.RawToken, auth?.subscription]);

  const addToCart = useCallback(
    async (video) => {
      if (hasActiveSubscription(auth?.subscription)) {
        return {
          success: false,
          message:
            "One-time purchases are not available while you have an active subscription. Use Download instead.",
        };
      }

      const isDuplicate = cartItems.some(
        (item) =>
          (item.videoID === video._id || item.video?._id === video._id)
      );

      if (isDuplicate) {
        return { success: false, message: "This video is already in your cart" };
      }

      if (cartItems.length >= 20) {
        return { success: false, message: "Cart limit reached (max 20 items)" };
      }

      const newItem = {
        videoID: video._id,
        video: {
          _id: video._id,
          name: video.name,
          thumnail: video.thumnail,
          previewURL: video.previewURL,
          duration: video.duration,
          orientation: video.orientation,
          fileType: video.fileType,
          shotonmobile: video.shotonmobile,
        },
        addedAt: new Date().toISOString(),
      };

      // For logged-in users, hit the server FIRST so we surface ownership /
      // duplicate / cart-limit errors before mutating local state. This is the
      // real-time ownership check requested for the "add to cart" button.
      if (auth?.RawToken) {
        try {
          const res = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/cart/add`,
            { videoID: video._id },
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${auth.RawToken}`,
              },
            }
          );

          if (!res.data?.success) {
            return {
              success: false,
              message: res.data?.message || "Could not add to cart",
            };
          }
        } catch (error) {
          const status = error?.response?.status;
          const serverMessage = error?.response?.data?.message;
          if (status === 400 && serverMessage) {
            return { success: false, message: serverMessage };
          }
          console.error("Error adding to server cart:", error);
          return {
            success: false,
            message: serverMessage || "Could not add to cart. Please try again.",
          };
        }
      }

      setCartItems((prev) => [...prev, newItem]);
      return { success: true, message: "Added to cart" };
    },
    [cartItems, auth?.RawToken, auth?.subscription]
  );

  const removeFromCart = useCallback(
    async (videoID) => {
      setCartItems((prev) =>
        prev.filter(
          (item) =>
            item.videoID !== videoID && item.video?._id !== videoID
        )
      );

      if (auth?.RawToken) {
        try {
          await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/cart/remove`,
            { videoID },
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${auth.RawToken}`,
              },
            }
          );
        } catch (error) {
          console.error("Error removing from server cart:", error);
        }
      }
    },
    [auth?.RawToken]
  );

  const clearCart = useCallback(async () => {
    setCartItems([]);

    if (auth?.RawToken) {
      try {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/cart/clear`,
          {},
          {
            headers: {
              Authorization: `Bearer ${auth.RawToken}`,
            },
          }
        );
      } catch (error) {
        console.error("Error clearing server cart:", error);
      }
    }
  }, [auth?.RawToken]);

  const isInCart = useCallback(
    (videoID) => {
      return cartItems.some(
        (item) =>
          item.videoID === videoID || item.video?._id === videoID
      );
    },
    [cartItems]
  );

  const cartCount = cartItems.length;

  return (
    <CartContext.Provider
      value={{
        cartItems,
        cartCount,
        addToCart,
        removeFromCart,
        clearCart,
        isInCart,
        setCartItems,
        revalidateCart,
        consumeRemovedOwned,
        lastRemovedOwned,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
